import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { evaluatePassword } from "../utils/passwordRules.js";

const panelV = {
  hidden: { opacity: 0, y: -4 },
  show:   { opacity: 1, y: 0, transition: { type: "spring", stiffness: 260, damping: 24 } },
  exit:   { opacity: 0, y: -4, transition: { duration: 0.15 } },
};

function RuleItem({ ok, label }) {
  return (
    <motion.li
      className={`flex items-center gap-2 text-xs ${ok ? "text-emerald-700" : "text-rose-700"}`}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 26 }}
    >
      {ok ? (
        <svg className="h-4 w-4 text-emerald-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" className="stroke-emerald-200" strokeWidth="2" />
          <path d="M7 12l3 3 7-7" className="stroke-emerald-600" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg className="h-4 w-4 text-rose-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="12" r="6" />
        </svg>
      )}
      <span>{label}</span>
    </motion.li>
  );
}

export default function PasswordChecklistLogin({ value = "", show = false, defaultCollapsed = false }) {
  const { fulfilled, missing, score } = evaluatePassword(value);
  const visible   = show && value.length > 0;
  const total     = fulfilled.length + missing.length || 1;
  const pct       = (score / total) * 100;
  const barColor  = score >= 5 ? "bg-emerald-500" : score >= 3 ? "bg-amber-500" : "bg-rose-500";

  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="pwd-checklist"
          variants={panelV}
          initial="hidden"
          animate="show"
          exit="exit"
          className="mt-3 rounded-xl border border-slate-200 bg-white p-3"
          aria-live="polite"
        >
          {/* Header row: title + score + toggle */}
          <div className="flex items-center justify-between gap-2 text-[11px] text-slate-600">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-700">Password strength</span>
              <span className="tabular-nums text-slate-500">{score}/{total}</span>
            </div>

            <button
              type="button"
              onClick={() => setCollapsed(c => !c)}
              aria-expanded={!collapsed}
              aria-controls="pwd-checklist-details"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-slate-600 hover:bg-slate-100 cursor-pointer"
              title={collapsed ? "Show details" : "Hide details"}
            >
              <span className="hidden sm:inline ">{collapsed ? "Show" : "Hide"} details</span>
              {/* chevron: up when expanded, down when collapsed */}
              <svg
                className={`h-4 w-4 transition-transform ${collapsed ? "" : "rotate-180"}`}
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                {/* chevron-down path */}
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {/* Strength bar (always visible) */}
          <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
          </div>

          {/* Collapsible details: Fulfilled / Missing */}
          <motion.div
            id="pwd-checklist-details"
            initial={false}
            animate={collapsed ? { height: 0, opacity: 0, marginTop: 0 } : { height: "auto", opacity: 1, marginTop: 12 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: "hidden" }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fulfilled.length > 0 && (
                <div>
                  <div className="text-[11px] font-medium text-emerald-700 mb-1">Fulfilled</div>
                  <ul className="space-y-1.5">
                    {fulfilled.map(r => <RuleItem key={r.id} ok label={r.label} />)}
                  </ul>
                </div>
              )}
              {missing.length > 0 && (
                <div>
                  <div className="text-[11px] font-medium text-rose-700 mb-1">Missing</div>
                  <ul className="space-y-1.5">
                    {missing.map(r => <RuleItem key={r.id} ok={false} label={r.label} />)}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
